import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { formatIDR, formatNumber, parseIDR, parsePrice, formatDate, parseQty, formatQty, todayISO } from "@/lib/format";
import { Card, PageHeader, Button, Field, Input, Select, Modal, Badge, Textarea } from "@/components/ui-kit";
import BulkImport from "@/components/BulkImport";
import FilePreview from "@/components/FilePreview";
import MasterCombo from "@/components/MasterCombo";
import { useTableFilter } from "@/hooks/useTableFilter";
import { usePagination } from "@/hooks/usePagination";
import { useAuth } from "@/contexts/AuthContext";
import { recordHistoryMany } from "@/lib/history";
import { PoDetailModal } from "@/components/po/PoDetailModal";
import { Plus, Pencil, Trash2, Upload, FileText, X, Eye, Download, Lock, Unlock } from "lucide-react";
import { DatePicker } from "@/components/DatePicker";

const blank = {
  po_number: "", contract_date: todayISO(), client_name: "", category: "delivery",
  items: [{ material_name: "", unit: "", quantity: 1, unit_price: 0, category: "delivery" }],
  notes: "", file_path: null,
  // Iter 65 repeat (#1) — PPN toggle pada PO. Default OFF supaya total tidak ter-minus saat ditagihkan.
  // Akan aktif otomatis jika tagihan menggunakan PPN (lihat backend invoices.py _auto_apply_ppn_to_po).
  use_ppn: false, ppn_rate: 11,
};

export default function PurchaseOrders() {
  const { t } = useLang();
  const { hasPerm } = useAuth();
  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(blank);
  const [manual, setManual] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewId, setPreviewId] = useState(null);
  const [detailPo, setDetailPo] = useState(null);  // Task 8: PO detail modal with file + quota

  const { filtered: textFiltered, FilterBar } = useTableFilter(rows, ["po_number", "client_name", "category", "contract_date", "items.material_name", "notes"], "Cari PO / klien / material…");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [poBillingFilter, setPoBillingFilter] = useState("");  // Iter15 #7 — billed/partial/unbilled
  const [categories, setCategories] = useState([]);
  // Iter v34m (#4) — Filter rentang tanggal kontrak PO
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const _byDate = (rs) => rs.filter((r) => {
    const d = (r.contract_date || "");
    if (dateFrom && d < dateFrom) return false;
    if (dateTo && d > dateTo) return false;
    return true;
  });
  const _byCat = categoryFilter ? _byDate(textFiltered).filter((r) => (r.category || "") === categoryFilter) : _byDate(textFiltered);
  const _statusPoOf = (po) => {
    if (po.fully_billed) return "billed";
    const sjs = po.sjs_summary || [];
    if (sjs.length === 0) return "unbilled";
    const billed = sjs.filter((s) => s.billed).length;
    if (billed === 0) return "unbilled";
    // Iter v34b4 (fix #4) — partial dihilangkan; sebagian pun dianggap "billed"
    return "billed";
  };
  const filtered = poBillingFilter ? _byCat.filter((r) => _statusPoOf(r) === poBillingFilter) : _byCat;
  const { pageItems, Pagination } = usePagination(filtered, { perPage: 14 });

  const load = async () => {
    const [posR, cR, catR] = await Promise.all([
      api.get("/pos"), api.get("/clients"), api.get("/masters/categories"),
    ]);
    setRows(posR.data); setClients(cR.data); setCategories(catR.data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEdit(null); setForm(blank); setManual(clients.length === 0); setOpen(true); };
  const openEdit = (r) => {
    setEdit(r);
    // Iter 65 repeat (#1) — ensure PPN fields default correctly when editing legacy PO.
    setForm({
      ...blank,
      ...r,
      items: r.items || blank.items,
      use_ppn: !!r.use_ppn,
      ppn_rate: r.ppn_rate ?? 11,
    });
    setManual(true);
    setOpen(true);
  };

  // Iter v34w (#1) — Approval workflow untuk PO yang dibuat klien.
  const approvePo = async (r) => {
    if (!window.confirm(`Setujui PO ${r.po_number}${r.pending_change?.type ? ` (perubahan: ${r.pending_change.type})` : ""}?`)) return;
    try {
      const res = await api.post(`/pos/${r.id}/approve`, { note: "" });
      toast.success(res.data?.deleted ? "PO dihapus (delete disetujui)" : "PO disetujui");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal approve"); }
  };
  const rejectPo = async (r) => {
    const note = window.prompt(`Alasan menolak PO ${r.po_number}?`, "");
    if (note === null) return;
    try {
      await api.post(`/pos/${r.id}/reject`, { note });
      toast.success("PO ditolak");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal reject"); }
  };
  const STATUS_LABEL = {
    pending_approval: { txt: "Menunggu Persetujuan", tone: "warn" },
    pending_edit: { txt: "Pending Edit (klien)", tone: "warn" },
    pending_delete: { txt: "Pending Hapus (klien)", tone: "warn" },
    rejected: { txt: "Ditolak", tone: "danger" },
    open: null,
  };

  const submit = async () => {
    try {
      const payload = {
        ...form,
        items: form.items.map((i) => ({ ...i, quantity: parseQty(i.quantity), unit_price: parsePrice(i.unit_price) })),
      };
      if (edit) await api.patch(`/pos/${edit.id}`, payload);
      else await api.post("/pos", payload);
      // Record autocomplete history for free-text fields
      const histPairs = [
        { field: "po.client_name", value: form.client_name },
        { field: "po.notes", value: form.notes },
      ];
      (form.items || []).forEach((it) => {
        histPairs.push({ field: "po.material_name", value: it.material_name });
        histPairs.push({ field: "po.unit", value: it.unit });
      });
      recordHistoryMany(histPairs);
      toast.success("PO " + (edit ? "diperbarui" : "dibuat"));
      setOpen(false); load();
    } catch (e) { toast.error("Gagal: " + (e?.response?.data?.detail || "")); }
  };

  const del = async (id) => { if (!window.confirm("Hapus PO?")) return; await api.delete(`/pos/${id}`); toast.success("Dihapus"); load(); };

  // Iter v34br (Task 10) — Tutup / Buka Kembali PO. PO tutup disembunyikan dari
  // Progres PO Dashboard, dropdown SJ Ready Mix/Material, dropdown Invoice.
  const closePo = async (po) => {
    if (!window.confirm(`Tutup PO ${po.po_number}?\n\nPO tidak akan muncul lagi di:\n· Progres PO (Dashboard)\n· Pembuatan Surat Jalan\n· Pembuatan Tagihan\n\nAnda tetap bisa "Buka Kembali" nanti.`)) return;
    try {
      await api.post(`/pos/${po.id}/close`);
      toast.success(`PO ${po.po_number} ditutup`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menutup PO"); }
  };
  const reopenPo = async (po) => {
    if (!window.confirm(`Buka kembali PO ${po.po_number}?`)) return;
    try {
      await api.post(`/pos/${po.id}/reopen`);
      toast.success(`PO ${po.po_number} dibuka kembali`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal membuka PO"); }
  };

  const addItem = () => setForm({ ...form, items: [...form.items, { material_name: "", unit: "", quantity: 1, unit_price: 0, category: "delivery" }] });
  const rmItem = (idx) => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) });
  const setItem = (idx, k, v) => {
    const items = [...form.items]; items[idx][k] = v; setForm({ ...form, items });
  };

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("kind", "po");
      const r = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm({ ...form, file_path: r.data.path, file_id: r.data.id, file_name: r.data.filename, file_content_type: r.data.content_type });
      toast.success("File terunggah");
    } catch (err) { toast.error("Gagal upload"); }
    finally { setUploading(false); }
  };

  return (
    <div className="min-h-full bg-[#f6f5f0]">
    <div className="max-w-[1600px] mx-auto p-4 md:p-6 lg:p-8 space-y-6">
      <PageHeader
        overline="Operasional · Pesanan"
        title={t("purchase_order")}
        description="Rekapitulasi PO & kuota. Lacak material terkirim, belum terkirim, ditagih, dan belum ditagih per kontrak."
        action={<div className="flex items-center gap-2">{hasPerm("po.create") && <BulkImport kind="pos" onDone={load} />}{hasPerm("po.create") && <Button onClick={openNew} data-testid="add-po-btn"><Plus className="h-4 w-4" />{t("input_edit_po")}</Button>}</div>}
      />

      {/* Bento KPI mini */}
      {(() => {
        // Iter v34bh — FIX: Field yang benar dari backend adalah `grand_total`/`total_value`,
        // bukan `total_amount` (yang tidak ada di response → semua stat jadi 0).
        // Untuk billed: `billed_total` (bukan `billed_amount`).
        const poTotal = (p) => Number(p.grand_total ?? p.total_value ?? p.po_total) || 0;
        const poBilled = (p) => Number(p.billed_total ?? p.billed_amount) || 0;
        const totalPo = rows.length;
        const billedCount = rows.filter((p) => p.fully_billed || (poBilled(p) >= poTotal(p) && poTotal(p) > 0)).length;
        const partialCount = rows.filter((p) => {
          const b = poBilled(p);
          const t = poTotal(p);
          return b > 0 && b < t;
        }).length;
        const unbilledCount = totalPo - billedCount - partialCount;
        // Iter v34cd (#8) — Total Nilai PO auto-filter ke bulan berjalan.
        // Match: contract_date atau date jatuh di bulan+tahun saat ini.
        const _now = new Date();
        const _ym = `${_now.getFullYear()}-${String(_now.getMonth() + 1).padStart(2, "0")}`;
        const _monthLabel = _now.toLocaleString("id-ID", { month: "long", year: "numeric" });
        const rowsMonth = rows.filter((p) => {
          const d = String(p.contract_date || p.date || "").slice(0, 7);
          return d === _ym;
        });
        const totalNilai = rowsMonth.reduce((s, p) => s + poTotal(p), 0);
        return (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div className="rounded-2xl bg-white border border-stone-200/70 p-4 col-span-2 md:col-span-2">
              <div className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">Total Nilai PO · {_monthLabel}</div>
              <div className="font-display text-3xl mt-1 text-stone-900 font-mono-num">{formatIDR(totalNilai)}</div>
              <div className="text-[10px] text-stone-500 mt-0.5">{rowsMonth.length} PO bulan ini · {totalPo} PO total</div>
            </div>
            <div className="rounded-2xl bg-emerald-50/60 border border-emerald-100 p-4">
              <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">Ditagihkan</div>
              <div className="font-display text-2xl mt-1 text-emerald-800">{billedCount}</div>
            </div>
            <div className="rounded-2xl bg-amber-50/60 border border-amber-200 p-4">
              <div className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold">Partial</div>
              <div className="font-display text-2xl mt-1 text-amber-800">{partialCount}</div>
            </div>
            <div className="rounded-2xl bg-blue-50/60 border border-blue-100 p-4">
              <div className="text-[10px] uppercase tracking-wider text-blue-700 font-semibold">Belum Ditagihkan</div>
              <div className="font-display text-2xl mt-1 text-blue-800">{unbilledCount}</div>
            </div>
          </div>
        );
      })()}

      <div className="flex items-center justify-between gap-3 flex-wrap">
        {FilterBar}
        <div className="flex items-center gap-2 flex-wrap">
          {/* Iter v34m (#4) — Date range filter */}
          <div className="flex items-center gap-1">
            <DatePicker value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="!w-auto !text-xs" title="Tanggal kontrak dari" data-testid="po-date-from" />
            <span className="text-stone-400 text-xs">—</span>
            <DatePicker value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="!w-auto !text-xs" title="Tanggal kontrak sampai" data-testid="po-date-to" />
            {(dateFrom || dateTo) && (
              <button onClick={() => { setDateFrom(""); setDateTo(""); }} className="text-stone-400 hover:text-stone-700 text-xs px-1" title="Reset tanggal" data-testid="po-date-reset">×</button>
            )}
          </div>
          <Select value={poBillingFilter} onChange={(e) => setPoBillingFilter(e.target.value)} className="!w-auto !text-xs" data-testid="po-status-filter">
            <option value="">Semua Status</option>
            <option value="billed">Sudah Ditagihkan</option>
            {/* Iter v34b4 (fix #4) — partial dihilangkan */}
            <option value="unbilled">Belum Ditagihkan</option>
          </Select>
          <Select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} className="!w-auto !text-xs" data-testid="po-category-filter">
            <option value="">Semua Kategori</option>
            {categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
          </Select>
        </div>
      </div>

      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-stone-50 border-b border-stone-200 text-left text-[10px] font-semibold uppercase tracking-wider text-stone-600">
              <th className="py-3 px-3">{t("contract_date")}</th>
              <th className="py-3 px-3">{t("po_number")}</th>
              <th className="py-3 px-3">{t("client_name")}</th>
              <th className="py-3 px-3">{t("category")}</th>
              <th className="py-3 px-3">Barang</th>
              <th className="py-3 px-3 text-right">{t("delivered")} / PO</th>
              <th className="py-3 px-3 text-right">Tertagih / PO</th>
              <th className="py-3 px-3 text-right">Nilai Tertagih</th>
              <th className="py-3 px-3 text-right">{t("unbilled")}</th>
              <th className="py-3 px-3">File</th>
              <th className="py-3 px-3 text-right">{t("actions")}</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={11} className="py-12 text-center text-stone-400">Tidak ada PO.</td></tr>
            )}
            {pageItems.map((r, idx) => {
              const unbilled = (r.po_total || 0) - (r.billed_total || 0);
              return (
                <tr key={r.id} className="border-b border-stone-100 hover:bg-stone-50/50" data-testid={`po-row-${idx}`}>
                  <td className="py-2.5 px-3 font-mono text-stone-600 align-top">{formatDate(r.contract_date)}</td>
                  <td className="py-2.5 px-3 font-mono align-top">
                    <div>{r.po_number}</div>
                    {STATUS_LABEL[r.status] && (
                      <Badge tone={STATUS_LABEL[r.status].tone} className="mt-1 text-[10px]" data-testid={`po-status-${idx}`}>
                        {STATUS_LABEL[r.status].txt}
                      </Badge>
                    )}
                    {r.created_by_klien && (
                      <div className="text-[9px] uppercase text-stone-400 mt-0.5">dari klien</div>
                    )}
                    {r.pending_change?.type && r.status === "open" && (
                      <Badge tone="warn" className="mt-1 text-[10px]">Pending {r.pending_change.type}</Badge>
                    )}
                    {/* Iter v34br (Task 10) — Badge "Ditutup" */}
                    {r.is_closed && (
                      <Badge tone="stone" className="mt-1 text-[10px] bg-stone-200" data-testid={`po-closed-badge-${idx}`}>
                        🔒 Ditutup
                      </Badge>
                    )}
                  </td>
                  <td className="py-2.5 px-3 font-medium align-top">{r.client_name}</td>
                  <td className="py-2.5 px-3 align-top"><Badge tone={r.category === "service" ? "info" : "terra"}>{r.category}</Badge></td>
                  <td className="py-2.5 px-3 align-top">
                    <ul className="space-y-1 text-sm">
                      {(r.items || []).map((it, j) => (
                        <li key={j} className="leading-tight">
                          <span className="text-stone-400">–</span> {it.material_name}
                          <span className="ml-1 text-xs text-stone-500">({formatQty(it.quantity)} {it.unit})</span>
                        </li>
                      ))}
                    </ul>
                  </td>
                  <td className="py-2.5 px-3 align-top">
                    <ul className="space-y-1 text-sm text-right">
                      {(r.items || []).map((it, j) => {
                        const dq = (r.delivered || {})[it.material_name] || 0;
                        return (
                          <li key={j} className="font-mono-num leading-tight">
                            <span className="text-emerald-700">{formatQty(dq)}</span>
                            <span className="text-stone-300"> / </span>
                            <span className="text-stone-700">{formatQty(it.quantity)}</span>
                          </li>
                        );
                      })}
                    </ul>
                  </td>
                  <td className="py-2.5 px-3 align-top" data-testid={`po-billed-qty-${idx}`}>
                    <ul className="space-y-1 text-sm text-right">
                      {(r.items || []).map((it, j) => {
                        const bq = (r.billed_qty || {})[it.material_name] || 0;
                        const total = parseFloat(it.quantity) || 0;
                        const full = total > 0 && bq >= total;
                        return (
                          <li key={j} className="font-mono-num leading-tight">
                            <span className={full ? "text-emerald-700 font-semibold" : "text-amber-700"}>{formatQty(bq)}</span>
                            <span className="text-stone-300"> / </span>
                            <span className="text-stone-700">{formatQty(total)}</span>
                          </li>
                        );
                      })}
                    </ul>
                  </td>
                  <td className="py-2.5 px-3 text-right font-mono-num align-top">{formatIDR(r.billed_total || 0)}</td>
                  <td className="py-2.5 px-3 text-right font-mono-num text-stone-500 align-top">{formatIDR(unbilled)}</td>
                  <td className="py-2.5 px-3 align-top">
                    <div className="flex items-center gap-1.5">
                      {r.file_id && (
                        <>
                          <button
                            onClick={() => setPreviewId({ id: r.file_id, name: r.file_name, ct: r.file_content_type })}
                            className="text-emerald-600 hover:text-emerald-700 inline-flex items-center gap-0.5 text-[10px] uppercase tracking-wider border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 px-1.5 py-0.5"
                            data-testid={`po-view-file-${idx}`}
                            title="Lihat file PO via popup"
                          >
                            <Eye className="h-3 w-3" /> Lihat
                          </button>
                          <a
                            href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${r.file_id}?download=1`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-stone-600 hover:text-stone-800 inline-flex items-center gap-0.5 text-[10px] uppercase tracking-wider border border-stone-200 bg-stone-50 hover:bg-stone-100 px-1.5 py-0.5"
                            data-testid={`po-download-file-${idx}`}
                            title="Download file PO"
                          >
                            <Download className="h-3 w-3" /> Unduh
                          </a>
                        </>
                      )}
                      <button onClick={() => setDetailPo(r)} className="text-stone-500 hover:text-terra-700" data-testid={`po-detail-${idx}`} title="Lihat detail PO + kuota">
                        <FileText className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                  <td className="py-2.5 px-3 text-right align-top">
                    {/* Iter v34w (#1) — Approve/Reject untuk PO menunggu persetujuan / pending edit / pending delete. */}
                    {hasPerm("po.edit") && ["pending_approval", "pending_edit", "pending_delete", "rejected"].includes(r.status) && (
                      <div className="flex items-center justify-end gap-1 mb-1">
                        {r.status !== "rejected" && (
                          <button onClick={() => approvePo(r)} className="text-emerald-700 hover:text-emerald-900 text-[10px] uppercase tracking-wider border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 px-1.5 py-0.5" data-testid={`po-approve-${idx}`} title="Setujui">
                            Setujui
                          </button>
                        )}
                        {r.status !== "rejected" && (
                          <button onClick={() => rejectPo(r)} className="text-red-700 hover:text-red-900 text-[10px] uppercase tracking-wider border border-red-200 bg-red-50 hover:bg-red-100 px-1.5 py-0.5" data-testid={`po-reject-${idx}`} title="Tolak">
                            Tolak
                          </button>
                        )}
                      </div>
                    )}
                    {hasPerm("po.edit") && <button onClick={() => openEdit(r)} className="text-stone-500 hover:text-terra-600 px-2" title="Edit PO"><Pencil className="h-3.5 w-3.5" /></button>}
                    {/* Iter v34br (Task 10) — Tutup / Buka Kembali PO. Perm: po.edit */}
                    {hasPerm("po.edit") && (
                      r.is_closed ? (
                        <button onClick={() => reopenPo(r)} className="text-emerald-600 hover:text-emerald-800 px-2" title="Buka Kembali PO" data-testid={`po-reopen-${idx}`}>
                          <Unlock className="h-3.5 w-3.5" />
                        </button>
                      ) : (
                        <button onClick={() => closePo(r)} className="text-stone-500 hover:text-amber-700 px-2" title="Tutup PO (sembunyikan dari SJ & Tagihan)" data-testid={`po-close-${idx}`}>
                          <Lock className="h-3.5 w-3.5" />
                        </button>
                      )
                    )}
                    {hasPerm("po.delete") && <button onClick={() => del(r.id)} className="text-stone-500 hover:text-red-700 px-2" title="Hapus PO"><Trash2 className="h-3.5 w-3.5" /></button>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>
      {Pagination}

      <Modal open={open} onClose={() => setOpen(false)} title={edit ? t("edit") + " PO" : t("input_edit_po")} size="xl">
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label={t("po_number")} required>
              <Input value={form.po_number} onChange={(e) => setForm({ ...form, po_number: e.target.value })} data-testid="form-po-number" />
            </Field>
            <Field label={t("contract_date")} required>
              <DatePicker value={form.contract_date} onChange={(e) => setForm({ ...form, contract_date: e.target.value })} data-testid="form-contract-date" />
            </Field>
            <Field label={t("category")}>
              <MasterCombo source="categories" value={form.category} onChange={(v) => setForm({ ...form, category: v })} placeholder="delivery, service…" data-testid="form-po-category" />
            </Field>
          </div>

          <Field label={t("client_name")}>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-xs text-stone-600">
                <input type="checkbox" checked={manual} onChange={(e) => setManual(e.target.checked)} data-testid="manual-client-toggle" /> Manual
              </label>
              {manual ? (
                <MasterCombo source="clients" value={form.client_name} onChange={(v) => setForm({ ...form, client_name: v })} placeholder="Tulis / pilih nama klien" data-testid="form-client-manual" className="flex-1" />
              ) : (
                <Select value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} data-testid="form-client-dropdown">
                  <option value="">-- Pilih klien --</option>
                  {clients.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                </Select>
              )}
            </div>
          </Field>

          {/* Items dynamic */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-stone-600">{t("items_stage")}</label>
              <Button variant="secondary" onClick={addItem} className="!py-1 !px-2 text-xs"><Plus className="h-3 w-3" />{t("add")}</Button>
            </div>
            <div className="border border-stone-200 divide-y divide-stone-100">
              {form.items.map((it, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 p-2 items-center">
                  <Input className="col-span-4" placeholder={t("material_name")} value={it.material_name} onChange={(e) => setItem(i, "material_name", e.target.value)} data-testid={`po-item-name-${i}`} />
                  <div className="col-span-2"><MasterCombo source="units" value={it.unit} onChange={(v) => setItem(i, "unit", v)} placeholder={t("unit")} /></div>
                  <Input className="col-span-2 font-mono-num" placeholder="Qty" value={it.quantity} onChange={(e) => setItem(i, "quantity", e.target.value)} />
                  <Input className="col-span-3 font-mono-num" placeholder={t("unit_price")} value={it.unit_price} onChange={(e) => setItem(i, "unit_price", e.target.value)} />
                  <button onClick={() => rmItem(i)} className="col-span-1 text-stone-400 hover:text-red-700"><X className="h-4 w-4 mx-auto" /></button>
                </div>
              ))}
            </div>
          </div>

          {/* Upload */}
          <Field label={t("upload_file") + " (JPG/PNG/PDF)"}>
            <div className="flex items-center gap-3">
              <label className="inline-flex items-center gap-2 px-3 py-2 border border-stone-300 cursor-pointer hover:bg-stone-50 text-sm">
                <Upload className="h-4 w-4" />
                <span>{uploading ? "Mengunggah…" : "Pilih file"}</span>
                <input type="file" accept=".jpg,.jpeg,.png,.pdf" onChange={uploadFile} className="hidden" data-testid="po-file-input" />
              </label>
              {form.file_path && (
                <>
                  <span className="text-xs text-emerald-700 inline-flex items-center gap-1">
                    <FileText className="h-3 w-3" /> {form.file_name || "Terunggah"}
                  </span>
                  {form.file_id && (
                    <button onClick={() => setPreviewId({ id: form.file_id, name: form.file_name, ct: form.file_content_type })} className="text-terra-600 hover:text-terra-700 inline-flex items-center gap-1 text-xs" type="button">
                      <Eye className="h-3.5 w-3.5" />Preview
                    </button>
                  )}
                </>
              )}
            </div>
          </Field>

          <Field label={t("notes")}>
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </Field>

          {/* Iter 65 repeat (#1) — PPN toggle (default OFF). */}
          <Card className="p-3 bg-stone-50/50 border-stone-200">
            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <input
                type="checkbox"
                checked={!!form.use_ppn}
                onChange={(e) => setForm({ ...form, use_ppn: e.target.checked })}
                data-testid="po-use-ppn"
              />
              <span className="font-semibold text-stone-800">Gunakan PPN pada PO ini</span>
            </label>
            {form.use_ppn && (
              <div className="mt-3 grid grid-cols-2 gap-3">
                <Field label="Tarif PPN (%)" hint="Default 11%. Otomatis dipakai saat invoice menggunakan PPN.">
                  <Input
                    type="number" min="0" max="100" step="0.5"
                    value={form.ppn_rate ?? 11}
                    onChange={(e) => setForm({ ...form, ppn_rate: e.target.value })}
                    data-testid="po-ppn-rate"
                  />
                </Field>
              </div>
            )}
            <p className="mt-2 text-[11px] text-stone-500">
              <b>Default OFF</b> — supaya total PO tidak ter-minus saat dipakai membuat tagihan. Akan
              <b> aktif otomatis</b> apabila operator membuat tagihan dengan PPN aktif (PO total akan
              di-recompute include PPN secara otomatis).
            </p>
          </Card>

          <div className="flex justify-end gap-2 pt-4 border-t border-stone-200">
            <Button variant="ghost" onClick={() => setOpen(false)}>{t("cancel")}</Button>
            <Button onClick={submit} data-testid="save-po-btn">{t("save")}</Button>
          </div>
        </div>
      </Modal>
      <FilePreview open={!!previewId} onClose={() => setPreviewId(null)} fileId={previewId?.id} filename={previewId?.name} contentType={previewId?.ct} />
      <PoDetailModal open={!!detailPo} onClose={() => setDetailPo(null)} po={detailPo} onRefresh={async () => { await load(); setDetailPo(null); }} />
    </div>
    </div>
  );
}
